/**
 * @fileoverview React Hook for Global Keyboard Shortcuts Management
 *
 * This module provides a React hook that manages global keyboard shortcuts for
 * search functionality and accessibility features. It handles cross-platform
 * keyboard combinations, prevents conflicts with user input, and provides
 * comprehensive accessibility shortcuts for users with different needs.
 *
 * Key Features:
 * - Global search shortcuts (Ctrl+Space, Cmd+Space, Ctrl+K, /)
 * - Accessibility shortcuts for screen readers and motor impairments
 * - Cross-platform compatibility (Windows, Mac, Linux)
 * - Input conflict prevention (doesn't trigger in input fields)
 * - Debouncing to prevent rapid-fire triggering
 * - Modal and overlay management shortcuts
 * - Zoom and display adjustment shortcuts
 *
 * Supported Shortcuts:
 * - Search: Ctrl+Space, Cmd+Space, Ctrl+K, /
 * - Accessibility: Alt+A (open tools), Alt+H (high contrast), Alt+T (large text)
 * - Reading: Alt+R (reading guide)
 * - Zoom: Alt+Plus/Minus
 * - Escape: Close modals/search
 *
 * @requires useSearch - Search context for search modal management
 * @requires useAccessibility - Accessibility context for a11y features
 *
 * @example
 * // Basic usage - just call the hook to enable shortcuts
 * const { shortcuts } = useKeyboardShortcuts();
 *
 * // Display available shortcuts to users
 * <div>
 *   <p>Search: {shortcuts.globalSearch}</p>
 *   <p>Quick search: {shortcuts.quickSearch}</p>
 *   <p>Accessibility: {shortcuts.accessibilityTools}</p>
 * </div>
 */

import { useEffect, useRef } from 'react';
import { useSearch } from '../context/SearchContext'; // eslint-disable-line no-unused-vars
import { useAccessibility } from '../context/AccessibilityContext';

/**
 * Custom React hook for managing global keyboard shortcuts
 *
 * Automatically sets up keyboard event listeners for various application
 * shortcuts including search, accessibility features, and modal management.
 * Handles cross-platform compatibility and prevents conflicts with user input.
 *
 * @returns {Object} Keyboard shortcuts interface
 * @returns {Object} returns.shortcuts - Object containing shortcut key combinations for display
 *
 * @example
 * const { shortcuts } = useKeyboardShortcuts();
 *
 * // All shortcuts are automatically active when this hook is used
 * // The shortcuts object contains display strings for UI:
 * console.log(shortcuts.globalSearch); // "Cmd+Space" on Mac, "Ctrl+Space" on PC
 * console.log(shortcuts.quickSearch);  // "/"
 * console.log(shortcuts.accessibilityTools); // "Alt+A"
 *
 * // Shortcuts automatically handle:
 * // - Search modal opening/closing
 * // - Accessibility feature toggles
 * // - Zoom level adjustments
 * // - Input field detection (won't trigger in forms)
 */
export const useKeyboardShortcuts = () => {
  // Search context no longer needed - search is handled in TopBar component
  const { 
    openModal, 
    closeModal, 
    isModalOpen,
    toggleHighContrast,
    toggleLargeText,
    toggleReadingGuide,
    setZoom,
    zoomLevel
  } = useAccessibility();
  const lastShortcutTime = useRef(0); // eslint-disable-line no-unused-vars
  const SHORTCUT_DEBOUNCE_MS = 200; // eslint-disable-line no-unused-vars

  useEffect(() => {
    /**
     * Main keyboard event handler for all global shortcuts
     *
     * Processes keydown events and triggers appropriate actions based on
     * key combinations. Includes safety checks to prevent triggering
     * shortcuts when user is typing in input fields.
     *
     * @param {KeyboardEvent} event - The keyboard event object
     */
    const handleKeyDown = (event) => {
      // Check if user is typing in an input/textarea/contenteditable
      const isTyping = event.target.tagName === 'INPUT' ||
                      event.target.tagName === 'TEXTAREA' ||
                      event.target.contentEditable === 'true';

      // Ctrl+; or Cmd+;: Autofill today's date in date inputs
      if ((event.ctrlKey || event.metaKey) && event.key === ';' && isTyping) {
        const target = event.target;

        // Check if the focused input is a date input
        if (target.tagName === 'INPUT' && target.type === 'date') {
          event.preventDefault();

          // Set today's date in YYYY-MM-DD format
          const today = new Date();
          const year = today.getFullYear();
          const month = String(today.getMonth() + 1).padStart(2, '0');
          const day = String(today.getDate()).padStart(2, '0');
          const dateString = `${year}-${month}-${day}`;

          // Get the native input setter to properly update React's state
          const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
            window.HTMLInputElement.prototype,
            'value'
          ).set;

          // Set the value using the native setter
          nativeInputValueSetter.call(target, dateString);

          // Create and dispatch a proper input event that React will recognize
          const inputEvent = new Event('input', { bubbles: true });
          target.dispatchEvent(inputEvent);

          // Also dispatch change event for good measure
          const changeEvent = new Event('change', { bubbles: true });
          target.dispatchEvent(changeEvent);

          return;
        }
      }

      // Note: Search shortcuts are now handled by the TopBar component
      // Ctrl+K / Cmd+K focuses the inline search input (no modal)
      // The old modal-based search (Ctrl+Space, /) has been removed

      // Accessibility keyboard shortcuts
      // On Mac, Option key is altKey, but sometimes produces different characters
      if (event.altKey || event.key === 'å' || event.key === '™' || event.key === '®' || event.key === 'Ω') {
        // Alt/Option+A: Open accessibility tools
        if (event.key === 'a' || event.key === 'å') {
          event.preventDefault();
          if (isModalOpen) {
            closeModal();
          } else {
            openModal();
          }
          return;
        }

        // Alt/Option+H: Toggle high contrast
        if (event.key === 'h' || event.key === '˙') {
          event.preventDefault();
          toggleHighContrast();
          return;
        }

        // Alt/Option+T: Toggle large text
        if (event.key === 't' || event.key === '†') {
          event.preventDefault();
          toggleLargeText();
          return;
        }

        // Alt/Option+R: Toggle reading guide
        if (event.key === 'r' || event.key === '®') {
          event.preventDefault();
          toggleReadingGuide();
          return;
        }

        // Alt+Plus: Zoom in
        if (event.key === '+' || event.key === '=') {
          event.preventDefault();
          if (zoomLevel < 200) {
            setZoom(zoomLevel + 10);
          }
          return;
        }

        // Alt+Minus: Zoom out
        if (event.key === '-') {
          event.preventDefault();
          if (zoomLevel > 50) {
            setZoom(zoomLevel - 10);
          }
          return;
        }
      }

      // Escape to close accessibility modal
      if (event.key === 'Escape' && isModalOpen) {
        event.preventDefault();
        closeModal();
        return;
      }
    };

    // Add event listener
    document.addEventListener('keydown', handleKeyDown, true);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [
    openModal,
    closeModal,
    isModalOpen,
    toggleHighContrast,
    toggleLargeText,
    toggleReadingGuide,
    setZoom,
    zoomLevel
  ]);

  /**
   * Object containing human-readable shortcut key combinations
   *
   * Provides platform-specific shortcut strings for display in UI elements
   * like help modals, tooltips, or keyboard shortcut reference guides.
   * Automatically detects Mac vs PC and shows appropriate modifier keys.
   *
   * Note: Search shortcuts (Cmd+K) are now handled in the TopBar component
   *
   * @type {Object}
   * @property {string} autofillDate - Autofill today's date in date inputs (Cmd+; or Ctrl+;)
   * @property {string} accessibilityTools - Open accessibility tools (Alt+A)
   * @property {string} highContrast - Toggle high contrast mode (Alt+H)
   * @property {string} largeText - Toggle large text mode (Alt+T)
   * @property {string} readingGuide - Toggle reading guide (Alt+R)
   * @property {string} zoomIn - Zoom in shortcut (Alt++)
   * @property {string} zoomOut - Zoom out shortcut (Alt+-)
   */
  const shortcuts = {
    // Date shortcuts
    autofillDate: navigator.platform.toUpperCase().indexOf('MAC') >= 0 ? 'Cmd+;' : 'Ctrl+;',
    // Accessibility shortcuts
    accessibilityTools: 'Alt+A',
    highContrast: 'Alt+H',
    largeText: 'Alt+T',
    readingGuide: 'Alt+R',
    zoomIn: 'Alt++',
    zoomOut: 'Alt+-'
  };

  return { shortcuts };
};